import { apiService } from './api';

/**
 * 🆕 NEW: Laboratory Methods API Service
 * Replaces hardcoded methods in ElementSelector with database-driven loading
 */
export class LaboratoryMethodsAPIService {
  private baseUrl = '/laboratory-methods';

  /**
   * Get multi-element methods formatted for ElementSelector
   * Replaces hardcoded MULTI_ELEMENT_METHODS
   */
  async getMultiElementMethods(): Promise<Record<string, any>> {
    try {
      const response = await apiService.get(`${this.baseUrl}/frontend/multi-element-methods`);
      return response;
    } catch (error) {
      console.error('❌ Error loading multi-element methods:', error);
      throw new Error('Failed to load methods from database');
    }
  }

  /**
   * Get method shortcuts formatted for ElementSelector
   * Replaces hardcoded methodShortcuts array
   */
  async getMethodShortcuts(): Promise<any[]> {
    try {
      const response = await apiService.get(`${this.baseUrl}/frontend/method-shortcuts`);
      return response;
    } catch (error) {
      console.error('❌ Error loading method shortcuts:', error);
      throw new Error('Failed to load method shortcuts from database');
    }
  }

  /**
   * Get all laboratory methods (for admin interface)
   */
  async getAllMethods(): Promise<any[]> {
    try {
      const response = await apiService.get(this.baseUrl);
      return response;
    } catch (error) {
      console.error('❌ Error loading all methods:', error);
      throw new Error('Failed to load all methods');
    }
  }

  /**
   * Get elements that have available methods
   */
  async getElementsWithMethods(): Promise<string[]> {
    try {
      const response = await apiService.get(`${this.baseUrl}/elements-with-methods`);
      return response;
    } catch (error) {
      console.error('❌ Error loading elements with methods:', error);
      throw new Error('Failed to load elements with methods');
    }
  }

  /**
   * Search methods by element
   */
  async searchMethodsByElement(element: string): Promise<any[]> {
    try {
      const response = await apiService.get(`${this.baseUrl}/search-by-element/${element}`);
      return response;
    } catch (error) {
      console.error(`❌ Error searching methods for element ${element}:`, error);
      throw new Error(`Failed to search methods for element ${element}`);
    }
  }

  /**
   * Create a new laboratory method
   */
  async createMethod(methodData: any): Promise<any> {
    try {
      const response = await apiService.post(this.baseUrl, methodData);
      return response;
    } catch (error) {
      console.error('❌ Error creating method:', error);
      throw new Error('Failed to create method');
    }
  }

  /**
   * Update an existing laboratory method
   */
  async updateMethod(id: string, methodData: any): Promise<any> {
    try {
      const response = await apiService.put(`${this.baseUrl}/${id}`, methodData);
      return response;
    } catch (error) {
      console.error(`❌ Error updating method ${id}:`, error);
      throw new Error('Failed to update method');
    }
  }

  /**
   * Delete a laboratory method
   */
  async deleteMethod(id: string): Promise<{ message: string }> {
    try {
      const response = await apiService.delete(`${this.baseUrl}/${id}`);
      return response;
    } catch (error) {
      console.error(`❌ Error deleting method ${id}:`, error);
      throw new Error('Failed to delete method');
    }
  }
}

// Create singleton instance
export const laboratoryMethodsAPIService = new LaboratoryMethodsAPIService();
export default laboratoryMethodsAPIService;
